import React from "react";
import { cn } from "@/lib/utils";
import { humanize } from "./proofApi";
import { FileCheck2, UserCheck, Sparkles, Globe, HelpCircle, AlertTriangle, ShieldCheck, Link2, Scale, FileText, Users, Gavel, ListChecks, BookOpen, GitBranch, Target, Shield, MessageSquareWarning } from "lucide-react";

const TONE = {
  success: "text-success bg-success-bg", warning: "text-warning bg-warning-bg", info: "text-info bg-info-bg",
  danger: "text-danger bg-danger-bg", muted: "text-ink-2 bg-secondary", copper: "text-copper bg-copper-light",
};

export const STATUS_TONE = {
  SUPPORTED: "success", PARTIALLY_SUPPORTED: "warning", UNSUPPORTED: "danger", CONTRADICTED: "danger", CONTESTED: "warning",
  ADMITTED: "success", DENIED: "muted", UNKNOWN: "muted", NOT_APPLICABLE: "muted", NEEDS_VERIFICATION: "info",
  BLOCKER: "danger", HIGH: "danger", MEDIUM: "warning", SUGGESTION: "info",
  OPEN: "warning", IN_REVIEW: "info", RESOLVED: "success", DISMISSED: "muted", SUPERSEDED: "muted", REOPENED: "warning",
  ACTIVE: "success", PROPOSED: "info", REJECTED: "muted",
};

export function Pill({ tone = "muted", children, className, ...rest }) {
  return <span className={cn("inline-flex items-center gap-1 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-medium", TONE[tone], className)} {...rest}>{children}</span>;
}

export function StatusPill({ status, ...rest }) {
  return <Pill tone={STATUS_TONE[status] || "muted"} {...rest}>{humanize(status)}</Pill>;
}

const VERIFY = {
  DOCUMENT_VERIFIED: ["Document Verified", "success", FileCheck2],
  ADVOCATE_VERIFIED: ["Advocate Verified", "success", UserCheck],
  SENIOR_VERIFIED: ["Senior Verified", "success", ShieldCheck],
  EXTERNAL_SOURCE_VERIFIED: ["External Verified", "success", Globe],
  SOURCE_LINKED: ["Source Linked", "info", Link2],
  NEEDS_LEGAL_VERIFICATION: ["Needs Legal Verification", "warning", AlertTriangle],
  PROPOSED: ["AI Suggested", "info", Sparkles],
  UNKNOWN: ["Unknown", "muted", HelpCircle],
};

export function VerificationChip({ node, ...rest }) {
  let key = node?.verificationStatus || "UNKNOWN";
  if (key === "PROPOSED" && node?.sourceType !== "AI_INFERENCE") key = "NEEDS_REVIEW";
  const [label, tone, Icon] = VERIFY[key] || ["Needs Review", "warning", HelpCircle];
  return <Pill tone={tone} title={`Source: ${humanize(node?.sourceType || "UNKNOWN")}`} {...rest}><Icon className="h-3 w-3" />{label}</Pill>;
}

export function SourceChip({ sourceType }) {
  const map = { USER_STATED: ["User Stated", "muted"], DOCUMENT_VERIFIED: ["Document", "success"], EXTERNAL_SOURCE_VERIFIED: ["External", "success"], AI_INFERENCE: ["AI Inference", "info"], UNKNOWN: ["Unknown", "muted"] };
  const [l, t] = map[sourceType] || map.UNKNOWN;
  return <Pill tone={t}>{l}</Pill>;
}

export const TYPE_ICON = {
  RELIEF: Target, CLAIM: Target, DEFENCE: Shield, ISSUE: Scale, LEGAL_REQUIREMENT: ListChecks, FACT: GitBranch, PLEADING_PROPOSITION: FileText,
  ADMISSION: UserCheck, DENIAL: MessageSquareWarning, DOCUMENT: FileText, DOCUMENT_EXTRACT: FileText, EVIDENCE_ARTIFACT: FileText, WITNESS: Users,
  TESTIMONY: Users, EXHIBIT: FileText, AUTHORITY: BookOpen, ORDER: Gavel, HEARING_NOTE: Gavel, ARGUMENT: MessageSquareWarning, CONFLICT: AlertTriangle,
};

export function TypeTag({ type, className }) {
  const Icon = TYPE_ICON[type] || HelpCircle;
  return <span className={cn("inline-flex items-center gap-1 rounded border border-line bg-surface px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-2", className)}><Icon className="h-3 w-3 text-copper" />{humanize(type)}</span>;
}

export function ConfidenceChip({ value }) {
  if (!value) return null;
  return <Pill tone="muted" title="Confidence in extraction/mapping — not legal success">{value.toLowerCase()} confidence</Pill>;
}
